import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuTreeNode } from '../types/menu';
import { SystemMenuBadge } from './SystemMenuBadge';

interface MenuTreeProps {
  nodes: MenuTreeNode[];
  selectedId: number | null;
  onSelect: (node: MenuTreeNode) => void;
  onDeleteRequest: (node: MenuTreeNode) => void;
  onReorder?: (parentId: number | null, items: { id: number; sortOrder: number }[]) => void;
  isReordering?: boolean;
  /** EXEC 샌드박스 모드: system_menu 행의 mutation 버튼을 disabled 처리 */
  isExecSandbox?: boolean;
}

interface TreeNodeRowProps {
  node: MenuTreeNode;
  selectedId: number | null;
  depth: number;
  siblings: MenuTreeNode[];
  siblingIndex: number;
  onSelect: (node: MenuTreeNode) => void;
  onDeleteRequest: (node: MenuTreeNode) => void;
  onMoveUp?: (node: MenuTreeNode, siblings: MenuTreeNode[], index: number) => void;
  onMoveDown?: (node: MenuTreeNode, siblings: MenuTreeNode[], index: number) => void;
  /** EXEC 샌드박스 모드에서 system_menu=true 행은 mutation 불가 */
  isExecSandbox?: boolean;
}

const MENU_TYPE_COLOR: Record<string, string> = {
  INTERNAL: 'var(--color-info)',
  EXTERNAL: 'var(--color-success)',
  DIVIDER: 'var(--color-text-muted)',
};

function TreeNodeRow({
  node,
  selectedId,
  depth,
  siblings,
  siblingIndex,
  onSelect,
  onDeleteRequest,
  onMoveUp,
  onMoveDown,
  isExecSandbox = false,
}: TreeNodeRowProps) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const isSelected = selectedId === node.id;
  const hasChildren = node.children && node.children.length > 0;
  const indentPx = depth * 20;
  // EXEC 샌드박스 모드에서 system_menu=true 행은 mutation 버튼 disabled
  const isSystemLocked = isExecSandbox && node.systemMenu;
  // 로케일 기준 표시명 — EN 모드에서 nameEn 우선, 없으면 nameKo. (i18n 미초기화 시 ko 기본)
  const isEn = (i18n?.language ?? 'ko').toLowerCase().startsWith('en');
  const displayName = isEn && node.nameEn?.trim() ? node.nameEn : node.nameKo;

  const typeColor = MENU_TYPE_COLOR[node.menuType] ?? 'var(--color-text-muted)';
  const MENU_TYPE_LABEL_I18N: Record<string, string> = {
    INTERNAL: t('menu.tree.action.internal'),
    EXTERNAL: t('menu.tree.action.external'),
    DIVIDER: t('menu.tree.action.divider'),
  };
  const typeLabel = MENU_TYPE_LABEL_I18N[node.menuType] ?? node.menuType;

  const handleRowClick = useCallback(() => {
    onSelect(node);
  }, [node, onSelect]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(node);
      }
    },
    [node, onSelect]
  );

  return (
    <li
      role="treeitem"
      aria-expanded={hasChildren ? expanded : undefined}
      aria-selected={isSelected}
      style={{ listStyle: 'none' }}
    >
      {/* Row — real <button> for reliable click handling in production builds (R7-072) */}
      <button
        type="button"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-3)',
          paddingLeft: `calc(var(--spacing-3) + ${indentPx}px)`,
          cursor: 'pointer',
          borderRadius: 'var(--radius-sm)',
          background: isSelected
            ? 'color-mix(in srgb, var(--color-brand-primary) 12%, var(--color-surface-muted))'
            : 'transparent',
          border: isSelected
            ? '1px solid color-mix(in srgb, var(--color-brand-primary) 40%, transparent)'
            : '1px solid transparent',
          opacity: node.visible ? 1 : 0.65,
          transition: 'background 0.1s',
          width: '100%',
          textAlign: 'left',
          fontFamily: 'inherit',
          fontSize: 'inherit',
        }}
        onClick={handleRowClick}
        onKeyDown={handleKeyDown}
        aria-label={t('menu.tree.aria.selected', {
          name: displayName,
          systemSuffix: node.systemMenu ? t('menu.tree.aria.systemSuffix') : '',
        })}
        aria-pressed={isSelected}
      >
        {/* Expand/Collapse toggle */}
        {hasChildren ? (
          <button
            type="button"
            aria-label={expanded ? t('menu.tree.aria.expanded') : t('menu.tree.aria.collapsed')}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              aria-hidden="true"
            >
              {expanded ? (
                <polyline points="6 9 12 15 18 9" />
              ) : (
                <polyline points="9 18 15 12 9 6" />
              )}
            </svg>
          </button>
        ) : (
          <span style={{ width: 12, flexShrink: 0 }} aria-hidden="true" />
        )}

        {/* Icon */}
        <span
          aria-hidden="true"
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            flexShrink: 0,
          }}
        >
          {node.menuType === 'DIVIDER' ? '📁' : node.menuType === 'EXTERNAL' ? '🔗' : '📄'}
        </span>

        {/* Name — 로케일 표시명 + 풀텍스트 hover title
            UAT 2026-06-01 #467: 기존 flex:1 + minWidth:240 이 메뉴명을 가용폭 전체로 확장시켜
            메뉴명과 배지(내부/자물쇠) 사이에 큰 빈 공간 + 깊은 들여쓰기 시 가로 스크롤바를 유발.
            → flex-grow 제거(내용폭만 차지) + minWidth:0 + ellipsis 로 배지를 메뉴명에 근접 배치하고
              긴 메뉴명은 패널 폭 내에서 말줄임 처리하여 가로 스크롤 제거. */}
        <span
          title={displayName}
          style={{
            flex: '0 1 auto',
            minWidth: 0,
            fontSize: 'var(--font-size-sm)',
            color: isSelected ? 'var(--color-brand-primary)' : 'var(--color-text-primary)',
            fontWeight: isSelected ? 600 : node.menuType === 'DIVIDER' ? 600 : 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
        </span>

        {/* Type badge */}
        <span
          aria-label={t('menu.tree.aria.typeLabel', { type: typeLabel })}
          style={{
            fontSize: '10px',
            fontWeight: 600,
            padding: '1px 4px',
            borderRadius: 'var(--radius-full)',
            border: `1px solid ${typeColor}`,
            color: typeColor,
            flexShrink: 0,
          }}
        >
          {typeLabel}
        </span>

        {/* Hidden badge */}
        {!node.visible && (
          <span
            aria-label={t('menu.tree.aria.hiddenMenu')}
            style={{
              fontSize: '10px',
              fontWeight: 600,
              padding: '1px 4px',
              borderRadius: 'var(--radius-full)',
              border: '1px solid var(--color-text-muted)',
              color: 'var(--color-text-muted)',
              flexShrink: 0,
            }}
          >
            {t('menu.tree.action.hidden')}
          </span>
        )}

        {/* System badge */}
        <SystemMenuBadge systemMenu={node.systemMenu} />

        {/* Order buttons */}
        {onMoveUp && siblingIndex > 0 && (
          <button
            type="button"
            aria-label={t('menu.tree.aria.moveUp', { name: node.nameKo })}
            title={isSystemLocked ? t('menuMgmt.sandbox.exec.lockedTooltip') : undefined}
            disabled={isSystemLocked}
            onClick={(e) => {
              e.stopPropagation();
              if (!isSystemLocked) onMoveUp(node, siblings, siblingIndex);
            }}
            style={{
              background: 'none',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '1px var(--spacing-1)',
              cursor: isSystemLocked ? 'not-allowed' : 'pointer',
              fontSize: '10px',
              color: 'var(--color-text-muted)',
              flexShrink: 0,
              opacity: isSystemLocked ? 0.45 : 1,
            }}
            aria-disabled={isSystemLocked}
          >
            ↑
          </button>
        )}
        {onMoveDown && siblingIndex < siblings.length - 1 && (
          <button
            type="button"
            aria-label={t('menu.tree.aria.moveDown', { name: node.nameKo })}
            title={isSystemLocked ? t('menuMgmt.sandbox.exec.lockedTooltip') : undefined}
            disabled={isSystemLocked}
            onClick={(e) => {
              e.stopPropagation();
              if (!isSystemLocked) onMoveDown(node, siblings, siblingIndex);
            }}
            style={{
              background: 'none',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '1px var(--spacing-1)',
              cursor: isSystemLocked ? 'not-allowed' : 'pointer',
              fontSize: '10px',
              color: 'var(--color-text-muted)',
              flexShrink: 0,
              opacity: isSystemLocked ? 0.45 : 1,
            }}
            aria-disabled={isSystemLocked}
          >
            ↓
          </button>
        )}

        {/* Delete button */}
        <button
          type="button"
          aria-label={
            isSystemLocked
              ? t('menuMgmt.sandbox.exec.lockedTooltip')
              : t('menu.tree.aria.delete', { name: node.nameKo })
          }
          title={isSystemLocked ? t('menuMgmt.sandbox.exec.lockedTooltip') : undefined}
          disabled={isSystemLocked}
          onClick={(e) => {
            e.stopPropagation();
            if (!isSystemLocked) onDeleteRequest(node);
          }}
          style={{
            background: 'none',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-sm)',
            padding: '1px var(--spacing-1)',
            cursor: isSystemLocked ? 'not-allowed' : 'pointer',
            fontSize: '10px',
            color: isSystemLocked ? 'var(--color-text-muted)' : 'var(--color-danger)',
            flexShrink: 0,
            opacity: isSystemLocked ? 0.45 : 1,
          }}
          aria-disabled={isSystemLocked}
        >
          {t('menu.tree.action.delete')}
        </button>
      </button>

      {/* Children */}
      {hasChildren && expanded && (
        <ul role="group" style={{ padding: 0, margin: 0 }}>
          {node.children.map((child, idx) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              selectedId={selectedId}
              depth={depth + 1}
              siblings={node.children}
              siblingIndex={idx}
              onSelect={onSelect}
              onDeleteRequest={onDeleteRequest}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              isExecSandbox={isExecSandbox}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * MenuTree — 재귀 트리 렌더.
 * - depth indent + 자물쇠 배지 (시스템 메뉴)
 * - ↑↓ 버튼으로 동일 parent 내 sortOrder 변경
 * - 선택된 노드는 onSelect 으로 부모에 알림
 */
export function MenuTree({
  nodes,
  selectedId,
  onSelect,
  onDeleteRequest,
  onReorder,
  isReordering = false,
  isExecSandbox = false,
}: MenuTreeProps) {
  const { t } = useTranslation();
  // Local mutable order state (shadow copy for optimistic UI)
  const [localNodes, setLocalNodes] = useState<MenuTreeNode[]>(nodes);
  const [isDirty, setIsDirty] = useState(false);

  // Sync external nodes when they change (e.g. after refetch)
  // We don't useEffect here to keep it simple — parent can force re-mount with key
  const effectiveNodes = isDirty ? localNodes : nodes;

  const moveNodeInSiblings = useCallback(
    (targetNode: MenuTreeNode, siblings: MenuTreeNode[], index: number, direction: 'up' | 'down') => {
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= siblings.length) return;

      // Update sortOrder in siblings array (swap)
      const newSiblings = siblings.map((s, i) => {
        if (i === index) return { ...s, sortOrder: siblings[swapIndex].sortOrder };
        if (i === swapIndex) return { ...s, sortOrder: siblings[index].sortOrder };
        return s;
      });

      // Sort by new sortOrder
      newSiblings.sort((a, b) => a.sortOrder - b.sortOrder);

      // Update localNodes recursively
      const updateInTree = (treeNodes: MenuTreeNode[]): MenuTreeNode[] => {
        return treeNodes.map((n) => {
          if (n.id === targetNode.parentId) {
            return { ...n, children: newSiblings };
          }
          if (n.children && n.children.length > 0) {
            return { ...n, children: updateInTree(n.children) };
          }
          return n;
        });
      };

      let updated: MenuTreeNode[];
      if (targetNode.parentId == null) {
        // root-level swap
        updated = newSiblings as MenuTreeNode[];
      } else {
        updated = updateInTree(localNodes);
      }

      setLocalNodes(updated);
      setIsDirty(true);
    },
    [localNodes]
  );

  const handleMoveUp = useCallback(
    (node: MenuTreeNode, siblings: MenuTreeNode[], index: number) => {
      moveNodeInSiblings(node, siblings, index, 'up');
    },
    [moveNodeInSiblings]
  );

  const handleMoveDown = useCallback(
    (node: MenuTreeNode, siblings: MenuTreeNode[], index: number) => {
      moveNodeInSiblings(node, siblings, index, 'down');
    },
    [moveNodeInSiblings]
  );

  const handleSaveOrder = useCallback(() => {
    if (!onReorder) return;
    // Collect all changed nodes and their new sortOrders at each level
    // Simple approach: collect root-level items
    const rootItems = effectiveNodes.map((n) => ({ id: n.id, sortOrder: n.sortOrder }));
    onReorder(null, rootItems);
    setIsDirty(false);
  }, [onReorder, effectiveNodes]);

  const handleCancelOrder = useCallback(() => {
    setLocalNodes(nodes);
    setIsDirty(false);
  }, [nodes]);

  if (effectiveNodes.length === 0) return null;

  return (
    <div>
      {/* Order action bar */}
      {isDirty && onReorder && (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: 'color-mix(in srgb, var(--color-brand-primary) 8%, var(--color-surface-muted))',
            border: '1px solid color-mix(in srgb, var(--color-brand-primary) 40%, transparent)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--spacing-2)',
            fontSize: 'var(--font-size-xs)',
          }}
        >
          <span style={{ flex: 1, color: 'var(--color-text-secondary)' }}>{t('menu.tree.orderChanged')}</span>
          <button
            type="button"
            disabled={isReordering}
            onClick={handleSaveOrder}
            style={{
              padding: '2px var(--spacing-3)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: isReordering ? 'not-allowed' : 'pointer',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              opacity: isReordering ? 0.7 : 1,
            }}
          >
            {isReordering ? t('menu.tree.orderSaving') : t('menu.tree.orderSave')}
          </button>
          <button
            type="button"
            onClick={handleCancelOrder}
            style={{
              padding: '2px var(--spacing-3)',
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {t('menu.tree.orderCancel')}
          </button>
        </div>
      )}

      <ul
        role="tree"
        aria-label={t('menu.tree.aria.label')}
        style={{ padding: 0, margin: 0 }}
      >
        {effectiveNodes.map((node, idx) => (
          <TreeNodeRow
            key={node.id}
            node={node}
            selectedId={selectedId}
            depth={0}
            siblings={effectiveNodes}
            siblingIndex={idx}
            onSelect={onSelect}
            onDeleteRequest={onDeleteRequest}
            onMoveUp={onReorder ? handleMoveUp : undefined}
            onMoveDown={onReorder ? handleMoveDown : undefined}
            isExecSandbox={isExecSandbox}
          />
        ))}
      </ul>
    </div>
  );
}
